import axios from 'axios';
import API_ENDPOINTS from './endpoints';

const axiosInstance = axios.create({
  baseURL: API_ENDPOINTS.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Variable to hold the store reference
let store = null;

// Function to inject the store (called after store creation)
export const injectStore = (_store) => {
  store = _store;
};

// Helper function to get token
const getToken = () => {
  // First try to get token from Redux store
  if (store) {
    try {
      const state = store.getState();
      if (state?.auth?.user?.token) {
        return state.auth.user.token;
      }
    } catch (e) {
      // Store not ready yet
    }
  }
  
  // Fallback to localStorage
  return localStorage.getItem('token');
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    
    if (token) {
      config.headers['x-api-key'] = token;
    }
    /* Superadmin organisation scope (OrgScopeSwitcher): the backend re-targets
       the session to this organisation for superadmins and ignores it for
       everyone else, so the Leads / Users screens show that tenant. */
    try {
      const scope = localStorage.getItem('orgScope');
      if (scope) config.headers['x-org-scope'] = scope;
    } catch (e) { /* storage unavailable */ }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for session handling
axiosInstance.interceptors.response.use(
  (response) => {
    // Check for logout response format:
    // { "status": true, "statusCode": 0, "response": [{ "status": 0, "message": "User logged out.", "sessionLoggedIn": 0 }] }
    // or any response with sessionLoggedIn: 0
    const responseData = response.data;
    const isLoggedOut = 
      responseData?.response?.[0]?.sessionLoggedIn === 0 ||
      (responseData?.status === true && 
       responseData?.statusCode === 0 && 
       responseData?.response?.[0]?.sessionLoggedIn === 0);

    if (isLoggedOut) {
      // Clear all localStorage
      localStorage.clear();
      
      // Redirect to login page
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      
      return Promise.reject(new Error('Session expired - user logged out'));
    }
    return response;
  },
  (error) => {
    // Handle error responses with logout indicator
    const errorData = error.response?.data;
    const isLoggedOut = 
      errorData?.response?.[0]?.sessionLoggedIn === 0 ||
      (errorData?.status === true && 
       errorData?.statusCode === 0 && 
       errorData?.response?.[0]?.sessionLoggedIn === 0);

    if (isLoggedOut || error.response?.status === 401) {
      localStorage.clear();
      
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
